import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import api from '../utils/api';
import { assessmentBasisLabel, assessmentBasisHint } from '../utils/assessmentBasis';
import {
  buildAttendanceExportFilename,
  getDownloadFilename,
  sessionIntakeLabel,
} from '../utils/download';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts';

// ─── WCAG-safe color tokens ───────────────────────────────────────────
// Sourced from the CSS custom properties in App.css so these follow the
// light/dark theme automatically — see App.css for the verified contrast
// ratios of --tmis-present/late/absent against both card and page surfaces.
const C = {
  present:     'var(--tmis-present)',
  presentBg:   'var(--tmis-chip-ongoing-bg)',   // darker chip fill; pair with presentOn
  presentOn:   'var(--tmis-chip-ongoing-text)',
  late:        'var(--tmis-late)',
  lateBg:      'var(--tmis-chip-late-bg)',
  lateOn:      'var(--tmis-chip-late-text)',
  mc:          'var(--tmis-mc)',
  mcBg:        'var(--tmis-chip-scheduled-bg)',
  mcOn:        'var(--tmis-chip-scheduled-text)',
  absent:      'var(--tmis-absent)',
  absentBg:    'var(--tmis-chip-cancelled-bg)',
  absentOn:    'var(--tmis-chip-cancelled-text)',
  rateHigh:    'var(--tmis-present)', // ≥ 80 %
  rateMid:     'var(--tmis-late)',    // 60–79 %
  rateLow:     'var(--tmis-absent)',  // < 60 %
  // Solid fill tokens (bar fills with white text on top) — constant across
  // themes, unlike present/late/absent which shift lighter in dark mode.
  presentBtn:  'var(--tmis-present-btn)',
  lateBtn:     'var(--tmis-late-btn)',
  absentBtn:   'var(--tmis-absent-btn)',
  muted:       'var(--tmis-muted)',
  navy:        'var(--tmis-heading)',
  teal:        'var(--tmis-teal)',    // text/border/highlight use — shifts lighter in dark mode
  tealBtn:     'var(--tmis-teal-btn)', // solid fill with white text on top — constant across themes
  cardBg:      'var(--tmis-card-bg)',
};

const formatSessionTime = value => value ? String(value).slice(0, 5) : '—';
const formatReportTime = value => value ? moment(value).format('HH:mm') : '—';

function rateColor(r) {
  return r >= 80 ? C.rateHigh : r >= 60 ? C.rateMid : C.rateLow;
}
// Text colour for use on top of rateBg (the darker chip fill).
function rateOn(r) {
  return r >= 80 ? C.presentOn : r >= 60 ? C.lateOn : C.absentOn;
}
function rateBg(r) {
  return r >= 80 ? C.presentBg : r >= 60 ? C.lateBg : C.absentBg;
}

// Attendance rate: (Present + Late + MC) / Total Sessions.
function attendanceRate({ total = 0, present = 0, late = 0, mc = 0 }) {
  return total > 0 ? Math.round(((present + late + mc) / total) * 1000) / 10 : 0;
}

// Rates are shown to 1 decimal place (e.g. 83.3%), matching the Excel '0.0%' format.
const fmtRate = r => `${(Number(r) || 0).toFixed(1)}%`;

// ─── searchable combobox (intakes, trainers) ─────────────────────────
function SearchCombobox({
  items, value, onChange, inputId, getKey, getLabel, getSub,
  placeholder, emptyText, loadingText,
}) {
  const [query, setQuery]           = useState('');
  const [open, setOpen]             = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef                = useRef(null);
  const inputRef                    = useRef(null);

  const selected = items.find(i => String(getKey(i)) === String(value)) || null;

  const filtered = query
    ? items.filter(i => getLabel(i).toLowerCase().includes(query.toLowerCase()))
    : items;

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false); setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleInputChange(e) {
    setQuery(e.target.value);
    setOpen(true);
    setHighlighted(0);
    if (!e.target.value) onChange('');
  }

  function handleSelect(item) {
    onChange(String(getKey(item)), item);
    setQuery(''); setOpen(false); setHighlighted(-1);
  }

  function handleClear() {
    onChange(''); setQuery(''); setOpen(false);
    inputRef.current?.focus();
  }

  function handleKeyDown(e) {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); if (highlighted >= 0 && filtered[highlighted]) handleSelect(filtered[highlighted]); }
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div className="input-group">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          className="form-control"
          placeholder={selected ? getLabel(selected) : (items.length ? placeholder : (loadingText || placeholder))}
          value={open ? query : (selected ? getLabel(selected) : '')}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        {value && (
          <button className="btn btn-outline-secondary" type="button" onClick={handleClear} tabIndex={-1} title="Clear">
            <i className="bi bi-x" />
          </button>
        )}
        <button
          className="btn btn-outline-secondary" type="button"
          onClick={() => { setOpen(o => !o); inputRef.current?.focus(); }}
          tabIndex={-1}
        >
          <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '0.75rem' }} />
        </button>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050,
          background: 'var(--tmis-card-bg)', border: '1px solid var(--tmis-border)', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 280, overflowY: 'auto', marginTop: 2,
        }}>
          {filtered.length === 0 ? (
            <div className="px-3 py-2" style={{ fontSize: '0.85rem', color: C.muted }}>{emptyText}</div>
          ) : filtered.map((item, idx) => {
            const sub = getSub ? getSub(item) : null;
            return (
              <div
                key={getKey(item)}
                onMouseDown={() => handleSelect(item)}
                onMouseEnter={() => setHighlighted(idx)}
                style={{
                  padding: '8px 14px', cursor: 'pointer',
                  background: idx === highlighted ? 'var(--tmis-list-hover-bg)' : 'transparent',
                  borderLeft: idx === highlighted ? `3px solid ${C.teal}` : '3px solid transparent',
                }}
              >
                <div className="fw-semibold" style={{ fontSize: '0.875rem', color: C.navy }}>{getLabel(item)}</div>
                {sub && <div style={{ fontSize: '0.75rem', color: C.muted }}>{sub}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function IntakeCombobox({ intakes, value, onChange }) {
  return (
    <SearchCombobox
      items={intakes}
      value={value}
      onChange={onChange}
      inputId="reports-intake"
      getKey={i => i.intake_code}
      getLabel={i => i.intake_code}
      getSub={i => i.soc_date
        ? `${moment(i.soc_date).format('DD MMM YYYY')} – ${moment(i.eoc_date).format('DD MMM YYYY')}`
        : null}
      placeholder="— Type to search intake —"
      emptyText="No intakes found."
    />
  );
}

function TrainerCombobox({ trainers, value, onChange }) {
  return (
    <SearchCombobox
      items={trainers}
      value={value}
      onChange={onChange}
      inputId="reports-trainer"
      getKey={t => t.id}
      getLabel={t => t.name}
      getSub={t => t.email || null}
      placeholder="— Type to search trainer —"
      loadingText="Loading trainers…"
      emptyText="No trainers found."
    />
  );
}

function formatSessionIntakes(value) {
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (typeof value !== 'string' || !value.trim()) return '—';
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed.join(', ') || '—') : String(parsed || '—');
  } catch (_) {
    return value;
  }
}

// ─── trainer report ──────────────────────────────────────────────────
function TrainerReport({ sessions, trainer, trainerId, startDate, endDate, onExport, exporting }) {
  const totals = sessions.reduce(
    (acc, session) => ({
      total:   acc.total   + session.summary.total,
      present: acc.present + session.summary.present,
      late:    acc.late    + session.summary.late,
      mc:      acc.mc      + (session.summary.mc || 0),
      absent:  acc.absent  + session.summary.absent,
    }),
    { total: 0, present: 0, late: 0, mc: 0, absent: 0 }
  );
  const overallRate = attendanceRate(totals);

  return (
    <>
      <div className="tmis-card reports-trainer-summary mb-4">
        <div className="reports-trainer-summary-heading">
          <div>
            <div className="reports-trainer-kicker">
              <i className="bi bi-person-badge me-2" aria-hidden="true" />Report by trainer
            </div>
            <h2>{trainer?.name || 'Selected trainer'}</h2>
            {trainer?.email && <div className="reports-trainer-email">{trainer.email}</div>}
            <div className="reports-trainer-range">
              <i className="bi bi-calendar3 me-1" aria-hidden="true" />
              {moment(startDate).format('DD MMM YYYY')} – {moment(endDate).format('DD MMM YYYY')}
              <span>Ended sessions only</span>
            </div>
          </div>
          <div className="reports-summary-stats d-flex align-items-center gap-2 flex-wrap">
            <StatBadge label="Sessions" value={sessions.length} textColor={C.teal} bgColor="var(--tmis-list-hover-bg)" />
            <StatBadge label="Present" value={totals.present} textColor={C.presentOn} bgColor={C.presentBg} />
            <StatBadge label="Late"    value={totals.late}    textColor={C.lateOn}    bgColor={C.lateBg}    />
            <StatBadge label="MC"      value={totals.mc}      textColor={C.mcOn}      bgColor={C.mcBg}      />
            <StatBadge label="Absent"  value={totals.absent}  textColor={C.absentOn}  bgColor={C.absentBg}  />
            <div className="reports-stat-badge d-flex flex-column align-items-center px-3 py-2 rounded-3"
              style={{ background: rateBg(overallRate), minWidth: 80 }}>
              <span className="fw-bold" style={{ fontSize: '1.25rem', color: rateOn(overallRate) }}>{fmtRate(overallRate)}</span>
              <span style={{ fontSize: '0.7rem', color: rateOn(overallRate) }}>Overall Rate</span>
            </div>
          </div>
        </div>
      </div>

      <div className="tmis-card reports-trainer-sessions-card p-0">
        <div className="reports-trainer-sessions-heading">
          <span><i className="bi bi-calendar3 me-2" aria-hidden="true" />Sessions and attendance rate</span>
          <span>{sessions.length} ended session{sessions.length === 1 ? '' : 's'}</span>
        </div>
        <div className="table-responsive reports-trainer-sessions-table">
          <table className="table tmis-table table-sm mb-0" aria-label="Trainer session attendance rates">
            <thead>
              <tr>
                <th className="text-center">Date</th>
                <th>Module</th>
                <th>Intake(s)</th>
                <th className="text-center">Type</th>
                <th className="text-center">Time</th>
                <th className="text-center">Trainer times</th>
                <th>Venue</th>
                <th className="text-center">Students</th>
                <th className="text-center">Rate</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(session => {
                const isAssessment = session.session_type === 'assessment';
                return (
                  <tr key={session.id}>
                    <td className="reports-trainer-nowrap text-center">{moment(session.session_date).format('DD MMM YYYY')}</td>
                    <td>
                      <div className="fw-semibold">{session.module_name || session.schedule_name || '—'}</div>
                      {session.schedule_name && session.module_name !== session.schedule_name && (
                        <small>{session.schedule_name}</small>
                      )}
                    </td>
                    <td>{formatSessionIntakes(session.intakes)}</td>
                    <td className="text-center">
                      <span
                        className="badge reports-trainer-type"
                        style={{
                          background: isAssessment ? 'var(--tmis-chip-late-bg)' : 'var(--tmis-chip-ongoing-bg)',
                          color: isAssessment ? 'var(--tmis-chip-late-text)' : 'var(--tmis-chip-ongoing-text)',
                        }}
                      >
                        {isAssessment ? (assessmentBasisLabel(session.assessment_basis) || 'Assessment') : 'Training'}
                      </span>
                    </td>
                    <td className="reports-trainer-nowrap text-center">{formatSessionTime(session.start_time)} – {formatSessionTime(session.end_time)}</td>
                    <td className="reports-trainer-time-cell text-center">
                      <div>Login: {formatReportTime(session.trainer_login_start)}</div>
                      <small>Logout: {formatReportTime(session.ended_at)}</small>
                    </td>
                    <td>{session.venue_name || '—'}</td>
                    <td className="text-center">{session.summary.total}</td>
                    <td className="text-center">
                      <span className="badge fw-bold rounded-pill px-2 reports-rate-badge"
                        style={{ background: rateBg(session.summary.rate), color: rateOn(session.summary.rate) }}>
                        {fmtRate(session.summary.rate)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="reports-trainer-rate-note">
          <i className="bi bi-info-circle me-1" aria-hidden="true" />
          Rate = (Present + Late + MC) ÷ attendance records. Pending attendance is not included in ended sessions.
        </div>
        <div className="reports-trainer-export-actions">
          <button
            className="btn btn-tmis-outline"
            type="button"
            onClick={() => onExport({ trainer_id: trainerId, start_date: startDate, end_date: endDate, include_students: false })}
            disabled={exporting}
          >
            {exporting ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-file-earmark-spreadsheet me-2" aria-hidden="true" />}
            Export summary
          </button>
          <button
            className="btn btn-tmis-primary"
            type="button"
            onClick={() => onExport({ trainer_id: trainerId, start_date: startDate, end_date: endDate, include_students: true })}
            disabled={exporting}
          >
            {exporting ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-file-earmark-person me-2" aria-hidden="true" />}
            Export with student records
          </button>
        </div>
      </div>
    </>
  );
}

// ─── stat badge (WCAG text colors) ───────────────────────────────────
function StatBadge({ label, value, textColor, bgColor }) {
  return (
    <div className="d-flex flex-column align-items-center px-3 py-2 rounded-3"
      style={{ background: bgColor, minWidth: 70 }}>
      <span className="fw-bold" style={{ fontSize: '1.1rem', color: textColor }}>{value}</span>
      <span style={{ fontSize: '0.7rem', color: textColor, whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
}

// ─── status badge pill ────────────────────────────────────────────────
function StatusBadge({ status }) {
  if (status === 'pending') return <span className="status-pending"><i className="bi bi-clock me-1" aria-hidden="true" />Pending Attendance</span>;
  if (status === 'present') return <span className="badge" style={{ background: C.presentBg, color: C.presentOn, fontWeight: 600 }}>Present</span>;
  if (status === 'late')    return <span className="badge" style={{ background: C.lateBg,    color: C.lateOn,    fontWeight: 600 }}>Late</span>;
  if (status === 'mc')      return <span className="badge" style={{ background: C.mcBg,      color: C.mcOn,      fontWeight: 600 }}>MC</span>;
  return                           <span className="badge" style={{ background: C.absentBg,  color: C.absentOn,  fontWeight: 600 }}>Absent</span>;
}

// ─── session card ─────────────────────────────────────────────────────
function SessionCard({ session, intakeCode, onExport }) {
  const [open, setOpen] = useState(false);
  const { summary, records } = session;
  const rc = rateOn(summary.rate);
  const rb = rateBg(summary.rate);
  const moduleCode = session.schedule_name || '—';
  const moduleName = session.module_name || moduleCode;

  return (
    <div className="tmis-card reports-session-card mb-3 p-0" style={{ overflow: 'hidden' }}>
      <div
        className="d-flex align-items-center gap-3 px-3 py-3"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(o => !o)}
      >
        {/* date block */}
        <div className="flex-shrink-0 text-center rounded-3 px-2 py-1"
          style={{ background: 'var(--tmis-table-row-hover)', minWidth: 56 }}>
          <div style={{ fontSize: '0.65rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {moment(session.session_date).format('MMM')}
          </div>
          <div className="fw-bold" style={{ fontSize: '1.15rem', lineHeight: 1, color: C.navy }}>
            {moment(session.session_date).format('DD')}
          </div>
        </div>

        {/* title + time */}
        <div className="flex-grow-1 reports-session-heading" style={{ minWidth: 0 }}>
          <div className="reports-session-title-row" style={{ minWidth: 0 }}>
            <span className="fw-semibold text-truncate" style={{ fontSize: '0.9rem', color: C.navy, minWidth: 0 }}>
              {moduleName}
            </span>
            {moduleName !== moduleCode && <span className="reports-session-module-code">({moduleCode})</span>}
            <span
              className="badge reports-session-type"
              style={{
                background: session.session_type === 'assessment' ? 'var(--tmis-chip-late-bg)' : 'var(--tmis-chip-ongoing-bg)',
                color: session.session_type === 'assessment' ? 'var(--tmis-chip-late-text)' : 'var(--tmis-chip-ongoing-text)',
                flexShrink: 0,
              }}
            >
              <i className={`bi ${session.session_type === 'assessment' ? 'bi-clipboard2-check-fill' : 'bi-mortarboard-fill'} me-1`} />
              {session.session_type === 'assessment' ? 'Assessment' : 'Training'}
            </span>
            {session.session_type === 'assessment' && !!session.assessment_basis && (
              <span
                className="badge reports-session-type"
                title={assessmentBasisHint(session.assessment_basis)}
                style={{ background: 'var(--tmis-chip-scheduled-bg)', color: 'var(--tmis-chip-scheduled-text)', flexShrink: 0 }}
              >
                <i className="bi bi-bar-chart-steps me-1" />
                {assessmentBasisLabel(session.assessment_basis)}
              </span>
            )}
          </div>
          <div className="reports-session-meta">
            <span><i className="bi bi-clock me-1" aria-hidden="true" />{formatSessionTime(session.start_time)} – {formatSessionTime(session.end_time)}</span>
            {session.venue_name && <span><i className="bi bi-geo-alt me-1" aria-hidden="true" />{session.venue_name}</span>}
            <span><i className="bi bi-people me-1" aria-hidden="true" />Intake(s): {formatSessionIntakes(session.intakes)}</span>
          </div>
          <div className="reports-session-meta reports-session-trainer-meta">
            <span><i className="bi bi-person-badge me-1" aria-hidden="true" />Trainer: {session.trainer_name || '—'}</span>
            <span><i className="bi bi-box-arrow-in-right me-1" aria-hidden="true" />Login: {formatReportTime(session.trainer_login_start)}</span>
            <span><i className="bi bi-box-arrow-right me-1" aria-hidden="true" />Trainer Logout: {formatReportTime(session.ended_at)}</span>
          </div>
        </div>

        {/* desktop stats */}
        <div className="d-none d-md-flex align-items-center gap-2 flex-shrink-0">
          <StatBadge label="Present" value={summary.present} textColor={C.presentOn} bgColor={C.presentBg} />
          <StatBadge label="Late"    value={summary.late}    textColor={C.lateOn}    bgColor={C.lateBg}    />
          <StatBadge label="MC"      value={summary.mc}      textColor={C.mcOn}      bgColor={C.mcBg}      />
          <StatBadge label="Absent"  value={summary.absent}  textColor={C.absentOn}  bgColor={C.absentBg}  />
          <div className="d-flex flex-column align-items-center px-3 py-2 rounded-3"
            style={{ background: rb, minWidth: 70 }}>
            <span className="fw-bold" style={{ fontSize: '1.1rem', color: rc }}>{fmtRate(summary.rate)}</span>
            <span style={{ fontSize: '0.7rem', color: rc }}>Rate</span>
          </div>
        </div>

        {/* mobile rate pill */}
        <div className="d-flex d-md-none flex-shrink-0">
          <span className="badge rounded-pill fw-bold reports-rate-badge"
            style={{ background: rb, color: rc, fontSize: '0.8rem' }}>
            {fmtRate(summary.rate)}
          </span>
        </div>

        <i className={`bi bi-chevron-${open ? 'up' : 'down'} flex-shrink-0`}
          style={{ color: C.muted, fontSize: '0.85rem' }} />
      </div>

      {/* mobile stat row */}
      <div className="d-flex d-md-none justify-content-around px-3 pb-2 gap-2">
        <StatBadge label="Present" value={summary.present} textColor={C.presentOn} bgColor={C.presentBg} />
        <StatBadge label="Late"    value={summary.late}    textColor={C.lateOn}    bgColor={C.lateBg}    />
        <StatBadge label="MC"      value={summary.mc}      textColor={C.mcOn}      bgColor={C.mcBg}      />
        <StatBadge label="Absent"  value={summary.absent}  textColor={C.absentOn}  bgColor={C.absentBg}  />
      </div>

      {/* expanded records */}
      {open && (
        <div className="reports-session-table-section">
          <div className="reports-session-table-heading">
            <span>
              <i className="bi bi-table me-2" aria-hidden="true" />
              Student attendance table
            </span>
            <span>{records.length} student{records.length === 1 ? '' : 's'}</span>
          </div>
          <div className="table-responsive reports-session-table">
            <table className="table tmis-table table-sm mb-0" style={{ fontSize: '0.82rem' }} aria-label="Student attendance records">
              <thead>
                <tr>
                  <th className="ps-3 text-center">#</th>
                  <th className="text-center">Student ID</th>
                  <th>Name</th>
                  <th className="d-none d-md-table-cell">Email</th>
                  <th className="text-center">Clock In</th>
                  <th className="d-none d-sm-table-cell text-center">Clock Out</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {records.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-3" style={{ color: C.muted }}>No records</td></tr>
                ) : records.map((r, i) => (
                  <tr key={r.id} style={{ background: i % 2 === 1 ? 'var(--tmis-table-row-hover)' : 'transparent' }}>
                    <td className="ps-3 text-center" style={{ color: C.muted }}>{i + 1}</td>
                    <td className="text-center" style={{ color: C.muted }}>{r.student_id || '—'}</td>
                    <td className="fw-medium" style={{ color: C.navy }}>{r.student_name}</td>
                    <td className="d-none d-md-table-cell" style={{ color: C.muted }}>{r.student_email || '—'}</td>
                    <td className="text-center" style={{ color: C.navy }}>{r.clock_in_time  ? moment(r.clock_in_time).format('HH:mm')  : '—'}</td>
                    <td className="d-none d-sm-table-cell text-center" style={{ color: C.navy }}>{r.clock_out_time ? moment(r.clock_out_time).format('HH:mm') : '—'}</td>
                    <td className="text-center"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="reports-session-table-footer d-flex justify-content-end px-3 py-2">
            <button
              className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
              onClick={() => onExport({ session_id: session.id, intake_code: intakeCode })}
            >
              <i className="bi bi-file-earmark-excel" />
              Export this session
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── overview tab ─────────────────────────────────────────────────────
function OverviewTab({ sessions, intakeCode, intakeDetail, onExport, exporting }) {
  // ── Chart 1: stacked bar per session (Present / Late / Absent) ──
  const sessionChartData = sessions.map(s => ({
    label: moment(s.session_date).format('DD MMM'),
    Present: s.summary.present,
    Late:    s.summary.late,
    MC:      s.summary.mc,
    Absent:  s.summary.absent,
    rate:    s.summary.rate,
  }));

  // ── Chart 2: student rate distribution ──
  const studentMap = {};
  for (const session of sessions) {
    for (const r of session.records) {
      const key = r.student_phone || r.student_email || r.student_name;
      if (!studentMap[key]) studentMap[key] = { total: 0, present: 0, late: 0, mc: 0, absent: 0 };
      studentMap[key].total++;
      if (r.status === 'present') studentMap[key].present++;
      else if (r.status === 'late') studentMap[key].late++;
      else if (r.status === 'mc') studentMap[key].mc++;
      else if (r.status === 'absent') studentMap[key].absent++;
    }
  }
  const studentRates = Object.values(studentMap).map(attendanceRate);
  const onTrack  = studentRates.filter(r => r >= 80).length;
  const atRisk   = studentRates.filter(r => r >= 60 && r < 80).length;
  const critical = studentRates.filter(r => r < 60).length;
  const total    = studentRates.length;

  const distData = [
    { label: 'On Track (≥80%)',  count: onTrack,  pct: total > 0 ? Math.round((onTrack  / total) * 100) : 0, color: C.present, fillColor: C.presentBtn, bg: C.presentBg },
    { label: 'At Risk (60–79%)', count: atRisk,   pct: total > 0 ? Math.round((atRisk   / total) * 100) : 0, color: C.late,    fillColor: C.lateBtn,    bg: C.lateBg    },
    { label: 'Critical (<60%)',  count: critical, pct: total > 0 ? Math.round((critical / total) * 100) : 0, color: C.absent,  fillColor: C.absentBtn,  bg: C.absentBg  },
  ];

  // overall totals
  const totals = sessions.reduce(
    (acc, s) => ({ total: acc.total + s.summary.total, present: acc.present + s.summary.present, late: acc.late + s.summary.late, mc: acc.mc + (s.summary.mc || 0), absent: acc.absent + s.summary.absent }),
    { total: 0, present: 0, late: 0, mc: 0, absent: 0 }
  );
  const overallRate = attendanceRate(totals);

  // Custom tooltip for session chart
  function SessionTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    const rate = payload[0]?.payload?.rate ?? 0;
    return (
      <div style={{ background: 'var(--tmis-card-bg)', border: '1px solid var(--tmis-border)', borderRadius: 8, padding: '10px 14px', fontSize: '0.82rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div className="fw-bold mb-1" style={{ color: C.navy }}>{label}</div>
        {payload.map(p => (
          <div key={p.name} style={{ color: p.name === 'Present' ? C.present : p.name === 'Late' ? C.late : p.name === 'MC' ? C.mc : C.absent }}>
            {p.name}: <strong>{p.value}</strong>
          </div>
        ))}
        <div className="mt-1" style={{ color: rateColor(rate), fontWeight: 600 }}>Rate: {fmtRate(rate)}</div>
      </div>
    );
  }

  return (
    <div>
      {/* ── key metrics row ── */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Total Sessions',  value: sessions.length,   color: C.navy,    bg: 'var(--tmis-table-row-hover)' },
          { label: 'Total Students',  value: total,             color: 'var(--tmis-chip-scheduled-text)', bg: 'var(--tmis-chip-scheduled-bg)' },
          { label: 'Overall Rate',    value: fmtRate(overallRate), color: rateOn(overallRate), bg: rateBg(overallRate) },
          { label: 'On Track (≥80%)',  value: onTrack,  color: C.presentOn, bg: C.presentBg },
          { label: 'At Risk (60–79%)', value: atRisk,   color: C.lateOn,    bg: C.lateBg    },
          { label: 'Critical (<60%)',  value: critical, color: C.absentOn,  bg: C.absentBg  },
        ].map(m => (
          <div key={m.label} className="col-6 col-sm-4 col-md-2">
            <div className="rounded-3 p-3 text-center h-100" style={{ background: m.bg }}>
              <div className="fw-bold" style={{ fontSize: '1.5rem', color: m.color, lineHeight: 1.1 }}>{m.value}</div>
              <div style={{ fontSize: '0.72rem', color: m.color, marginTop: 4 }}>{m.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── legend note ── */}
      <div className="d-flex flex-wrap gap-3 mb-4 px-1" style={{ fontSize: '0.78rem', color: C.muted }}>
        <span><strong style={{ color: C.navy }}>Overall Rate</strong> — (Present + Late + MC) / Total Sessions</span>
        <span style={{ borderLeft: '1px solid var(--tmis-border)', paddingLeft: 12 }}>
          <strong style={{ color: C.present }}>On Track ≥80%</strong> &nbsp;·&nbsp;
          <strong style={{ color: C.late }}>At Risk 60–79%</strong> &nbsp;·&nbsp;
          <strong style={{ color: C.absent }}>Critical &lt;60%</strong>
          &nbsp;— based on each student's individual attendance rate across all sessions in this intake
        </span>
      </div>

      {/* ── Chart 1: Session Attendance Trend ── */}
      <div className="tmis-card mb-4">
        <div className="fw-bold mb-1" style={{ color: C.navy, fontSize: '0.95rem' }}>
          <i className="bi bi-bar-chart-stacked me-2" />Session Attendance Trend
        </div>
        <div style={{ fontSize: '0.78rem', color: C.muted, marginBottom: 16 }}>
          Present / Late / MC / Absent per session
        </div>
        {sessionChartData.length === 0 ? (
          <div className="text-center py-4" style={{ color: C.muted }}>No session data.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={sessionChartData} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}
              barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--tmis-border)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<SessionTooltip />} />
              <Legend wrapperStyle={{ fontSize: '0.8rem', paddingTop: 8 }} />
              <Bar dataKey="Present" stackId="a" fill={C.present} radius={[0, 0, 0, 0]} />
              <Bar dataKey="Late"    stackId="a" fill={C.late}    radius={[0, 0, 0, 0]} />
              <Bar dataKey="MC"      stackId="a" fill={C.mc}      radius={[0, 0, 0, 0]} />
              <Bar dataKey="Absent"  stackId="a" fill={C.absent}  radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Chart 2: Student Rate Distribution ── */}
      <div className="tmis-card mb-3">
        <div className="fw-bold mb-1" style={{ color: C.navy, fontSize: '0.95rem' }}>
          <i className="bi bi-people me-2" />Student Rate Distribution
        </div>
        <div style={{ fontSize: '0.78rem', color: C.muted, marginBottom: 20 }}>
          How students are distributed across attendance rate brackets
        </div>
        {total === 0 ? (
          <div className="text-center py-4" style={{ color: C.muted }}>No student data.</div>
        ) : (
          <div>
            {distData.map(d => (
              <div key={d.label} className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-semibold" style={{ fontSize: '0.85rem', color: d.color }}>{d.label}</span>
                  <span style={{ fontSize: '0.85rem', color: C.muted }}>
                    <strong style={{ color: d.color }}>{d.count}</strong> student{d.count !== 1 ? 's' : ''} · {d.pct}%
                  </span>
                </div>
                <div style={{ height: 28, background: 'var(--tmis-table-row-hover)', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: d.pct + '%',
                    background: d.fillColor,
                    borderRadius: 6,
                    minWidth: d.count > 0 ? 28 : 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8,
                    transition: 'width 0.6s ease',
                  }}>
                    {d.pct >= 12 && (
                      <span style={{ fontSize: '0.75rem', color: '#fff', fontWeight: 700 }}>{d.pct}%</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── export button ── */}
      <div className="d-flex justify-content-end">
        <button
          className="btn btn-tmis-primary d-flex align-items-center gap-2"
          onClick={() => onExport({ intake_code: intakeCode })}
          disabled={exporting}
        >
          {exporting ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-file-earmark-excel" />}
          Export All
        </button>
      </div>
    </div>
  );
}

// ─── students tab ─────────────────────────────────────────────────────
function StudentsTab({ sessions, intakeCode }) {
  const [sortCol, setSortCol]           = useState('rate');
  const [sortDir, setSortDir]           = useState('asc');
  const [expandedKey, setExpandedKey]   = useState(null);
  const [pageSize, setPageSize]         = useState(10);   // students per page
  const [page, setPage]                 = useState(1);
  const [brkPageSize, setBrkPageSize]   = useState(5);
  const [brkPages, setBrkPages]         = useState({});
  const [brkSorts, setBrkSorts]         = useState({});   // { [studentKey]: { col, dir } }
  const BRK_DEFAULT_SORT = { col: 'session_date', dir: 'desc' };

  const PAGE_OPTS = [10, 20, 50, 100, 'ALL'];
  const BRK_OPTS  = [5, 10, 20, 'ALL'];

  // Aggregate per student across all sessions
  const students = useMemo(() => {
    const map = {};
    for (const session of sessions) {
      for (const r of session.records) {
        const key = r.student_phone || r.student_email || r.student_name;
        if (!map[key]) {
          map[key] = {
            studentId: r.student_id || '—',
            name:     r.student_name,
            email:    r.student_email || '—',
            total:    0,
            present:  0,
            late:     0,
            mc:       0,
            absent:   0,
            pending:  0,
            sessions: [], // per-session breakdown
          };
        }
        // Deduplicate by session_id — one row per student per session.
        // If multiple records exist, keep the best status (present > late > mc > absent > pending).
        const STATUS_RANK = { present: 5, late: 4, mc: 3, absent: 2, pending: 1 };
        const existingIdx = map[key].sessions.findIndex(s => s.session_id === session.id);
        if (existingIdx === -1) {
          // First record for this session — add it and count it
          map[key].total++;
          if (r.status === 'present')   map[key].present++;
          else if (r.status === 'late') map[key].late++;
          else if (r.status === 'mc')   map[key].mc++;
          else if (r.status === 'absent') map[key].absent++;
          else if (r.status === 'pending') map[key].pending++;
          map[key].sessions.push({
            session_id:     session.id,
            schedule_name:  session.schedule_name,
            session_date:   session.session_date,
            start_time:     session.start_time,
            status:         r.status,
            clock_in_time:  r.clock_in_time,
            clock_out_time: r.clock_out_time,
          });
        } else {
          // Duplicate record for same session — upgrade status if better, update times
          const existing = map[key].sessions[existingIdx];
          if ((STATUS_RANK[r.status] || 0) > (STATUS_RANK[existing.status] || 0)) {
            // Undo old count, apply new
            if (existing.status === 'present')   map[key].present--;
            else if (existing.status === 'late') map[key].late--;
            else if (existing.status === 'mc')   map[key].mc--;
            else if (existing.status === 'absent') map[key].absent--;
            else if (existing.status === 'pending') map[key].pending--;
            if (r.status === 'present')          map[key].present++;
            else if (r.status === 'late')        map[key].late++;
            else if (r.status === 'mc')          map[key].mc++;
            else if (r.status === 'absent')      map[key].absent++;
            else if (r.status === 'pending')    map[key].pending++;
            existing.status         = r.status;
            existing.clock_in_time  = r.clock_in_time;
            existing.clock_out_time = r.clock_out_time;
          }
        }
      }
    }
    return Object.values(map).map(s => {
      return {
        ...s,
        attended: s.present + s.late,
        rate: attendanceRate(s),
      };
    });
  }, [sessions]);

  function toggleSort(col) {
    setPage(1);
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  const sorted = useMemo(() => {
    return [...students].sort((a, b) => {
      const va = a[sortCol]; const vb = b[sortCol];
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [students, sortCol, sortDir]);

  const totalPages = pageSize === 'ALL' ? 1 : Math.ceil(sorted.length / pageSize);
  const paged = pageSize === 'ALL' ? sorted : sorted.slice((page - 1) * pageSize, page * pageSize);

  // ── reusable pagination bar ──
  function PaginationBar({ current, total, onPage, size, setSize, opts, label }) {
    return (
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2"
        style={{ borderTop: '1px solid var(--tmis-border)', background: 'var(--tmis-table-row-hover)', fontSize: '0.8rem' }}>
        <div className="d-flex align-items-center gap-2">
          <span style={{ color: C.muted }}>{label} per page:</span>
          {opts.map(o => (
            <button key={o} onClick={() => { setSize(o); onPage(1); }}
              style={{
                padding: '2px 8px', borderRadius: 4, border: '1px solid',
                borderColor: size === o ? C.tealBtn : 'var(--tmis-border)',
                background:  size === o ? C.tealBtn : C.cardBg,
                color:       size === o ? 'white' : C.navy,
                fontWeight:  size === o ? 700 : 400,
                cursor: 'pointer', fontSize: '0.78rem',
              }}>
              {o}
            </button>
          ))}
        </div>
        {size !== 'ALL' && total > 1 && (
          <div className="d-flex align-items-center gap-1">
            <button onClick={() => onPage(1)}       disabled={current === 1}      className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-double-left" /></button>
            <button onClick={() => onPage(current - 1)} disabled={current === 1}  className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-left" /></button>
            <span style={{ color: C.muted, minWidth: 70, textAlign: 'center' }}>Page {current} of {total}</span>
            <button onClick={() => onPage(current + 1)} disabled={current === total} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-right" /></button>
            <button onClick={() => onPage(total)}   disabled={current === total}  className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 7px' }}><i className="bi bi-chevron-double-right" /></button>
          </div>
        )}
      </div>
    );
  }

  function SortTh({ col, label, className = '', center = false }) {
    const active = sortCol === col;
    // The thead is always a solid navy bar (--tmis-navy doesn't shift with
    // theme), so header text must be a constant light color here rather
    // than the theme-adaptive C.navy/C.teal (those resolve to dark navy in
    // light mode — dark text on a dark bar — invisible).
    return (
      <th
        className={`${className}${center ? ' text-center' : ''}`.trim()}
        onClick={() => toggleSort(col)}
        style={{ cursor: 'pointer', color: active ? 'var(--tmis-teal-on-navy)' : 'var(--tmis-white)', userSelect: 'none', whiteSpace: 'nowrap' }}
      >
        {label}
        <i className={`bi ms-1 ${active ? (sortDir === 'asc' ? 'bi-sort-up' : 'bi-sort-down') : 'bi-arrow-down-up'}`}
          style={{ fontSize: '0.7rem', opacity: active ? 1 : 0.6 }} />
      </th>
    );
  }

  if (students.length === 0) {
    return (
      <div className="tmis-card text-center py-5" style={{ color: C.muted }}>
        <i className="bi bi-people fs-2 d-block mb-2" />
        No student records found for <strong>{intakeCode}</strong>.
      </div>
    );
  }

  // summary totals
  const totals = students.reduce((acc, s) => ({
    total:   acc.total   + s.total,
    present: acc.present + s.present,
    late:    acc.late    + s.late,
    mc:      acc.mc      + (s.mc || 0),
    absent:  acc.absent  + s.absent,
  }), { total: 0, present: 0, late: 0, mc: 0, absent: 0 });
  const overallRate = attendanceRate(totals);

  return (
    <>
      {/* summary strip */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <span style={{ fontSize: '0.85rem', color: C.muted }}>
          {students.length} student{students.length !== 1 ? 's' : ''} · {sessions.length} session{sessions.length !== 1 ? 's' : ''}
        </span>
        {/* <div className="ms-auto d-flex gap-2 flex-wrap">
          <StatBadge label="Present" value={totals.present} textColor={C.presentOn} bgColor={C.presentBg} />
          <StatBadge label="Late"    value={totals.late}    textColor={C.lateOn}    bgColor={C.lateBg}    />
          <StatBadge label="Absent"  value={totals.absent}  textColor={C.absentOn}  bgColor={C.absentBg}  />
          <div className="d-flex flex-column align-items-center px-3 py-2 rounded-3"
            style={{ background: rateBg(overallRate), minWidth: 80 }}>
            <span className="fw-bold" style={{ fontSize: '1.1rem', color: rateOn(overallRate) }}>{fmtRate(overallRate)}</span>
            <span style={{ fontSize: '0.7rem', color: rateOn(overallRate) }}>Avg Rate</span>
          </div>
        </div> */}
      </div>

      {/* table */}
      <div className="tmis-card p-0" style={{ overflow: 'hidden' }}>
        <div className="table-responsive">
          <table className="table tmis-table table-hover mb-0 table-striped" style={{ fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th className="ps-4 text-center" style={{ width: 36 }}>#</th>
                <SortTh col="studentId" label="Student ID" className="d-none d-sm-table-cell" center />
                <SortTh col="name"    label="Name" />
                <SortTh col="total"   label="Sessions" className="d-none d-sm-table-cell" center />
                <SortTh col="present" label="Present"  className="d-none d-md-table-cell" center />
                <SortTh col="late"    label="Late"     className="d-none d-md-table-cell" center />
                <SortTh col="mc"      label="MC"       className="d-none d-md-table-cell" center />
                <SortTh col="absent"  label="Absent"   className="d-none d-md-table-cell" center />
                <SortTh col="attended" label="Attended" className="d-none d-sm-table-cell" center />
                <SortTh col="rate"    label="Rate %" center />
              </tr>
            </thead>
            <tbody>
              {paged.map((s, i) => {
                const globalIdx = pageSize === 'ALL' ? i : (page - 1) * pageSize + i;
                const rc  = rateOn(s.rate);
                const rb  = rateBg(s.rate);
                const key = s.name + s.email;
                const isExpanded = expandedKey === key;
                // sort breakdown by current sort state (default: date desc)
                const brkSort = brkSorts[key] || BRK_DEFAULT_SORT;
                const sessionsSorted = [...(s.sessions || [])].sort((a, b) => {
                  let va = a[brkSort.col], vb = b[brkSort.col];
                  if (brkSort.col === 'session_date') { va = new Date(va); vb = new Date(vb); }
                  else if (brkSort.col === 'clock_in_time' || brkSort.col === 'clock_out_time') {
                    va = va ? new Date(va) : new Date(0); vb = vb ? new Date(vb) : new Date(0);
                  }
                  const cmp = va < vb ? -1 : va > vb ? 1 : 0;
                  return brkSort.dir === 'asc' ? cmp : -cmp;
                });
                return (
                  <>
                    <tr
                      key={key}
                      style={{
                        background: isExpanded ? 'var(--tmis-list-hover-bg)' : (i % 2 === 1 ? 'var(--tmis-table-row-hover)' : 'transparent'),
                        cursor: 'pointer',
                      }}
                      onClick={() => setExpandedKey(isExpanded ? null : key)}
                    >
                      <td className="ps-4 text-center" style={{ color: C.muted }}>{globalIdx + 1}</td>
                      <td className="d-none d-sm-table-cell text-center" style={{ color: C.muted }}>{s.studentId}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}
                            style={{ fontSize: '0.7rem', color: C.teal }} />
                          <div>
                            <div className="fw-semibold" style={{ color: C.navy }}>{s.name}</div>
                            <div style={{ fontSize: '0.75rem', color: C.muted }}>{s.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="d-none d-sm-table-cell text-center" style={{ color: C.navy }}>{s.total}</td>
                      <td className="d-none d-md-table-cell text-center">
                        <span style={{ color: C.present, fontWeight: 600 }}>{s.present}</span>
                      </td>
                      <td className="d-none d-md-table-cell text-center">
                        <span style={{ color: C.late, fontWeight: 600 }}>{s.late}</span>
                      </td>
                      <td className="d-none d-md-table-cell text-center">
                        <span style={{ color: C.mc, fontWeight: 600 }}>{s.mc}</span>
                      </td>
                      <td className="d-none d-md-table-cell text-center">
                        <span style={{ color: C.absent, fontWeight: 600 }}>{s.absent}</span>
                      </td>
                      <td className="d-none d-sm-table-cell text-center" style={{ color: C.navy }}>{s.attended}</td>
                      <td className="text-center">
                        <span className="badge fw-bold rounded-pill px-2 reports-rate-badge"
                          style={{ background: rb, color: rc, fontSize: '0.8rem' }}>
                          {fmtRate(s.rate)}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (() => {
                      const brkPage    = brkPages[key] || 1;
                      const brkSort    = brkSorts[key] || BRK_DEFAULT_SORT;
                      const brkTotal   = brkPageSize === 'ALL' ? 1 : Math.ceil(sessionsSorted.length / brkPageSize);
                      const brkPaged   = brkPageSize === 'ALL' ? sessionsSorted : sessionsSorted.slice((brkPage - 1) * brkPageSize, brkPage * brkPageSize);
                      const setBrkPage = (p) => setBrkPages(prev => ({ ...prev, [key]: p }));
                      const toggleBrkSort = (col) => {
                        setBrkSorts(prev => {
                          const cur = prev[key] || BRK_DEFAULT_SORT;
                          return { ...prev, [key]: { col, dir: cur.col === col ? (cur.dir === 'asc' ? 'desc' : 'asc') : 'desc' } };
                        });
                        setBrkPage(1);
                      };
                      const BrkTh = ({ col, label }) => {
                        const active = brkSort.col === col;
                        return (
                          <th className={`reports-breakdown-sort text-center ${active ? 'is-active' : ''}`} onClick={() => toggleBrkSort(col)}>
                            {label}
                            <i className={`bi ms-1 ${active ? (brkSort.dir === 'asc' ? 'bi-sort-up' : 'bi-sort-down') : 'bi-arrow-down-up'}`}
                              style={{ fontSize: '0.65rem', opacity: active ? 1 : 0.7 }} />
                          </th>
                        );
                      };
                      return (
                        <tr key={key + '_expanded'}>
                          <td colSpan={9} style={{ padding: 0, background: 'var(--tmis-list-hover-bg)' }}>
                            <div className="reports-breakdown-content">
                              <div className="reports-breakdown-toolbar">
                                <span className="fw-semibold reports-breakdown-heading">
                                  <i className="bi bi-calendar3 me-1" />Session Breakdown
                                  <span style={{ color: C.muted, fontWeight: 400, marginLeft: 6 }}>({sessionsSorted.length} session{sessionsSorted.length !== 1 ? 's' : ''})</span>
                                </span>
                                {/* breakdown page size selector */}
                                <div className="reports-breakdown-page-size">
                                  <span style={{ color: C.muted }}>Show:</span>
                                  {BRK_OPTS.map(o => (
                                    <button key={o}
                                      onClick={() => { setBrkPageSize(o); setBrkPage(1); }}
                                      style={{
                                        padding: '1px 6px', borderRadius: 4, border: '1px solid',
                                        borderColor: brkPageSize === o ? C.tealBtn : 'var(--tmis-border-strong)',
                                        background:  brkPageSize === o ? C.tealBtn : C.cardBg,
                                        color:       brkPageSize === o ? 'white' : C.navy,
                                        fontWeight:  brkPageSize === o ? 700 : 400,
                                        cursor: 'pointer', fontSize: '0.72rem',
                                      }}>
                                      {o}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="reports-breakdown-scroll">
                                <table className="reports-breakdown-table">
                                  <thead>
                                    <tr>
                                      <BrkTh col="session_date"   label="Date" />
                                      <BrkTh col="schedule_name"  label="Session" />
                                      <BrkTh col="start_time"     label="Time" />
                                      <BrkTh col="status"         label="Status" />
                                      <BrkTh col="clock_in_time"  label="Clock In" />
                                      <BrkTh col="clock_out_time" label="Clock Out" />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {brkPaged.map((ss, si) => (
                                      <tr key={ss.session_id}
                                        style={{ background: si % 2 === 1 ? 'var(--tmis-table-row-hover)' : 'transparent' }}>
                                        <td style={{ padding: '5px 8px', color: C.navy, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                          {moment(ss.session_date).format('DD MMM YYYY')}
                                        </td>
                                        <td style={{ padding: '5px 8px', color: C.navy, textAlign: 'center' }}>{ss.schedule_name}</td>
                                        <td style={{ padding: '5px 8px', color: C.muted, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                          {String(ss.start_time || '').slice(0, 5)}
                                        </td>
                                        <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                          <StatusBadge status={ss.status} />
                                        </td>
                                        <td style={{ padding: '5px 8px', color: C.muted, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                          {ss.clock_in_time ? moment(ss.clock_in_time).format('HH:mm') : '—'}
                                        </td>
                                        <td style={{ padding: '5px 8px', color: C.muted, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                          {ss.clock_out_time ? moment(ss.clock_out_time).format('HH:mm') : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {/* breakdown pagination */}
                              {brkPageSize !== 'ALL' && brkTotal > 1 && (
                                <div className="d-flex align-items-center justify-content-end gap-1 mt-2" style={{ fontSize: '0.75rem' }}>
                                  <button onClick={() => setBrkPage(1)}           disabled={brkPage === 1}        className="btn btn-sm btn-outline-secondary" style={{ padding: '1px 6px' }}><i className="bi bi-chevron-double-left" /></button>
                                  <button onClick={() => setBrkPage(brkPage - 1)} disabled={brkPage === 1}        className="btn btn-sm btn-outline-secondary" style={{ padding: '1px 6px' }}><i className="bi bi-chevron-left" /></button>
                                  <span style={{ color: C.muted, minWidth: 70, textAlign: 'center' }}>Page {brkPage} of {brkTotal}</span>
                                  <button onClick={() => setBrkPage(brkPage + 1)} disabled={brkPage === brkTotal} className="btn btn-sm btn-outline-secondary" style={{ padding: '1px 6px' }}><i className="bi bi-chevron-right" /></button>
                                  <button onClick={() => setBrkPage(brkTotal)}    disabled={brkPage === brkTotal} className="btn btn-sm btn-outline-secondary" style={{ padding: '1px 6px' }}><i className="bi bi-chevron-double-right" /></button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
        <PaginationBar
          current={page} total={totalPages} onPage={setPage}
          size={pageSize} setSize={(s) => { setPageSize(s); setPage(1); }}
          opts={PAGE_OPTS}
          label="Students"
        />
      </div>
    </>
  );
}

// ─── main page ────────────────────────────────────────────────────────
export default function ReportsPage() {
  const [intakes, setIntakes]               = useState([]);
  const [selectedIntake, setSelectedIntake] = useState('');
  const [intakeDetail, setIntakeDetail]     = useState(null);
  const [sessions, setSessions]             = useState([]);
  const [reportMode, setReportMode]         = useState('intake');
  const [trainers, setTrainers]             = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState('');
  const [trainerDetail, setTrainerDetail]   = useState(null);
  const [trainerSessions, setTrainerSessions] = useState([]);
  const [trainerStartDate, setTrainerStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [trainerEndDate, setTrainerEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [trainerSearched, setTrainerSearched] = useState(false);
  const [loading, setLoading]               = useState(false);
  const [exporting, setExporting]           = useState(false);
  const [activeTab, setActiveTab]           = useState('overview'); // 'overview' | 'sessions' | 'students'
  const today = moment().format('YYYY-MM-DD');

  useEffect(() => {
    api.get('/api/reports/intakes')
      .then(r => setIntakes(r.data.data || []))
      .catch(() => toast.error('Failed to load intakes.'));
  }, []);

  useEffect(() => {
    if (reportMode !== 'trainer' || trainers.length > 0) return;
    api.get('/api/users/trainers')
      .then(r => setTrainers(r.data.data || []))
      .catch(() => toast.error('Failed to load trainers.'));
  }, [reportMode, trainers.length]);

  const loadSessions = useCallback(async (intakeCode, detail) => {
    if (!intakeCode) { setSessions([]); setIntakeDetail(null); return; }
    setLoading(true);
    try {
      const r = await api.get('/api/reports/intake-sessions', { params: { intake_code: intakeCode } });
      setSessions((r.data.data || []).filter(session => session.status === 'ended'));
      setIntakeDetail(detail);
    } catch {
      toast.error('Failed to load sessions.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTrainerSessions = useCallback(async (trainerId, startDate, endDate) => {
    setLoading(true);
    try {
      const r = await api.get('/api/reports/trainer-sessions', {
        params: { trainer_id: trainerId, start_date: startDate, end_date: endDate },
      });
      setTrainerSessions((r.data.data || []).filter(session => session.status === 'ended'));
      setTrainerDetail(r.data.trainer || trainers.find(t => String(t.id) === String(trainerId)) || null);
      setTrainerSearched(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load trainer sessions.');
    } finally {
      setLoading(false);
    }
  }, [trainers]);

  function switchReportMode(mode) {
    setReportMode(mode);
    setSelectedIntake('');
    setIntakeDetail(null);
    setSessions([]);
    setSelectedTrainer('');
    setTrainerDetail(null);
    setTrainerSessions([]);
    setTrainerSearched(false);
    setActiveTab('overview');
  }

  function handleTrainerSearch() {
    if (!selectedTrainer) { toast.warn('Please select a trainer.'); return; }
    if (!trainerStartDate || !trainerEndDate) { toast.warn('Please choose a start and end date.'); return; }
    if (trainerStartDate > today || trainerEndDate > today) {
      toast.warn('Future dates cannot be selected.');
      return;
    }
    if (trainerStartDate > trainerEndDate) { toast.warn('The start date must be before the end date.'); return; }
    loadTrainerSessions(selectedTrainer, trainerStartDate, trainerEndDate);
  }

  async function handleExport({ session_id, intake_code, start_date, end_date, trainer_id, include_students }) {
    setExporting(true);
    try {
      const params = {};
      if (trainer_id)       { params.trainer_id = trainer_id; params.start_date = start_date; params.end_date = end_date; params.include_students = include_students ? '1' : '0'; }
      else if (session_id)  { params.session_id = session_id; params.intake_code = intake_code; }
      else if (intake_code) { params.intake_code = intake_code; }
      else if (start_date)  { params.start_date = start_date; params.end_date = end_date; }
      const res = await api.get('/api/reports/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const exportedSession = session_id
        ? sessions.find(session => String(session.id) === String(session_id))
        : null;
      const selectedTrainerDetail = trainerDetail || trainers.find(trainer => String(trainer.id) === String(trainer_id));
      const fallbackFilename = buildAttendanceExportFilename({
        date: trainer_id ? end_date : (exportedSession?.session_date || sessions[0]?.session_date || end_date || start_date),
        intakeCode: trainer_id
          ? `Trainer-${selectedTrainerDetail?.name || trainer_id}`
          : (intake_code || sessionIntakeLabel(sessions)),
        moduleCode: trainer_id ? null : (exportedSession?.schedule_name || null),
        period: trainer_id ? (include_students ? 'trainer-students' : 'trainer-summary') : undefined,
      });
      a.download = getDownloadFilename(res.headers, fallbackFilename);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported.');
    } catch {
      toast.error('Export failed.');
    } finally {
      setExporting(false);
    }
  }

  // overall banner totals (sessions tab)
  const totals = sessions.reduce(
    (acc, s) => ({
      total:   acc.total   + s.summary.total,
      present: acc.present + s.summary.present,
      late:    acc.late    + s.summary.late,
      mc:      acc.mc      + (s.summary.mc || 0),
      absent:  acc.absent  + s.summary.absent,
    }),
    { total: 0, present: 0, late: 0, mc: 0, absent: 0 }
  );
  const overallRate  = attendanceRate(totals);

  const hasResults = reportMode === 'intake' && !loading && selectedIntake && sessions.length > 0;
  const hasTrainerResults = reportMode === 'trainer'
    && !loading && trainerSearched && selectedTrainer && trainerSessions.length > 0;

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Reports</h1>
        <p>Attendance reports by intake or trainer</p>
      </div>

      {/* report mode and filters */}
      <div className="tmis-card reports-filter-card mb-4">
        <div className="reports-filter-header">
          <div>
            <h6 className="fw-bold mb-1" style={{ color: C.navy }}>Report type</h6>
            <p className="reports-filter-help mb-0">Choose how you want to review ended attendance sessions.</p>
          </div>
          <div className="filter-tab-group reports-mode-tabs" role="tablist" aria-label="Report type">
            <button
              className={`filter-tab ${reportMode === 'intake' ? 'active' : ''}`}
              type="button"
              role="tab"
              aria-selected={reportMode === 'intake'}
              onClick={() => switchReportMode('intake')}
            >
              <i className="bi bi-people me-1" aria-hidden="true" />By Intake
            </button>
            <button
              className={`filter-tab ${reportMode === 'trainer' ? 'active' : ''}`}
              type="button"
              role="tab"
              aria-selected={reportMode === 'trainer'}
              onClick={() => switchReportMode('trainer')}
            >
              <i className="bi bi-person-badge me-1" aria-hidden="true" />By Trainer
            </button>
          </div>
        </div>

        {reportMode === 'intake' ? (
          <div className="reports-filter-control">
            <label className="form-label fw-semibold" htmlFor="reports-intake">Select Intake</label>
            <IntakeCombobox
              intakes={intakes}
              value={selectedIntake}
              onChange={(code, detail) => {
                setSelectedIntake(code);
                setActiveTab('overview');
                loadSessions(code, detail || intakes.find(i => i.intake_code === code) || null);
              }}
            />
          </div>
        ) : (
          <div className="reports-trainer-filters">
            <div className="reports-trainer-filter-control reports-trainer-filter-trainer">
              <label className="form-label fw-semibold" htmlFor="reports-trainer">Select Trainer</label>
              <TrainerCombobox
                trainers={trainers}
                value={selectedTrainer}
                onChange={id => { setSelectedTrainer(id); setTrainerSearched(false); }}
              />
            </div>
            <div className="reports-trainer-filter-control">
              <label className="form-label fw-semibold" htmlFor="reports-trainer-start">From</label>
              <input
                id="reports-trainer-start"
                type="date"
                className="form-control"
                value={trainerStartDate}
                max={today}
                onChange={e => {
                  const nextDate = e.target.value;
                  setTrainerStartDate(nextDate && nextDate > today ? today : nextDate);
                  setTrainerSearched(false);
                }}
              />
            </div>
            <div className="reports-trainer-filter-control">
              <label className="form-label fw-semibold" htmlFor="reports-trainer-end">To</label>
              <input
                id="reports-trainer-end"
                type="date"
                className="form-control"
                value={trainerEndDate}
                max={today}
                onChange={e => {
                  const nextDate = e.target.value;
                  setTrainerEndDate(nextDate && nextDate > today ? today : nextDate);
                  setTrainerSearched(false);
                }}
              />
            </div>
            <button
              className="btn btn-tmis-primary reports-trainer-search"
              type="button"
              onClick={handleTrainerSearch}
              disabled={loading}
            >
              {loading ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-search me-2" aria-hidden="true" />}
              View report
            </button>
          </div>
        )}
      </div>

      {/* loading */}
      {loading && (
        <div className="d-flex align-items-center gap-2 mb-4" style={{ color: C.muted }}>
          <span className="spinner-border spinner-border-sm" />
          Loading…
        </div>
      )}

      {hasTrainerResults && (
        <TrainerReport
          sessions={trainerSessions}
          trainer={trainerDetail || trainers.find(t => String(t.id) === String(selectedTrainer))}
          trainerId={selectedTrainer}
          startDate={trainerStartDate}
          endDate={trainerEndDate}
          onExport={handleExport}
          exporting={exporting}
        />
      )}

      {/* results */}
      {hasResults && (
        <>
          {/* summary banner */}
          <div className="tmis-card reports-summary-card mb-0 d-flex flex-wrap align-items-center justify-content-between gap-3"
            style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: '1px solid var(--tmis-border)' }}>
            <div>
              <div className="fw-bold" style={{ fontSize: '1.05rem', color: C.navy }}>{selectedIntake}</div>
              {intakeDetail && (
                <div style={{ fontSize: '0.8rem', color: C.muted }}>
                  {moment(intakeDetail.soc_date).format('DD MMM YYYY')} – {moment(intakeDetail.eoc_date).format('DD MMM YYYY')}
                  &nbsp;·&nbsp; {sessions.length} session{sessions.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
            <div className="reports-summary-stats d-flex align-items-center gap-2 flex-wrap">
              <StatBadge label="Present" value={totals.present} textColor={C.presentOn} bgColor={C.presentBg} />
              <StatBadge label="Late"    value={totals.late}    textColor={C.lateOn}    bgColor={C.lateBg}    />
              <StatBadge label="MC"      value={totals.mc}      textColor={C.mcOn}      bgColor={C.mcBg}      />
              <StatBadge label="Absent"  value={totals.absent}  textColor={C.absentOn}  bgColor={C.absentBg}  />
              <div className="reports-stat-badge d-flex flex-column align-items-center px-3 py-2 rounded-3"
                style={{ background: rateBg(overallRate), minWidth: 80 }}>
                <span className="fw-bold" style={{ fontSize: '1.25rem', color: rateOn(overallRate) }}>{fmtRate(overallRate)}</span>
                <span style={{ fontSize: '0.7rem', color: rateOn(overallRate) }}>Overall Rate</span>
              </div>

            </div>
          </div>

          {/* tabs */}
          <div className="tmis-card reports-tabs-card mb-4 p-0"
            style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
            <div className="reports-tab-list">
              <button className={`reports-tab-button ${activeTab === 'overview' ? 'is-active' : ''}`} type="button" onClick={() => setActiveTab('overview')}>
                <i className="bi bi-grid me-2" />Overview
              </button>
              <button className={`reports-tab-button ${activeTab === 'sessions' ? 'is-active' : ''}`} type="button" onClick={() => setActiveTab('sessions')}>
                <i className="bi bi-calendar3 me-2" />Sessions
              </button>
              <button className={`reports-tab-button ${activeTab === 'students' ? 'is-active' : ''}`} type="button" onClick={() => setActiveTab('students')}>
                <i className="bi bi-people me-2" />Students
              </button>
            </div>

            <div className="p-3">
              {activeTab === 'overview' && (
                <OverviewTab
                  sessions={sessions}
                  intakeCode={selectedIntake}
                  intakeDetail={intakeDetail}
                  onExport={handleExport}
                  exporting={exporting}
                />
              )}
              {activeTab === 'sessions' && (
                sessions.map(s => (
                  <SessionCard
                    key={s.id}
                    session={s}
                    intakeCode={selectedIntake}
                    onExport={handleExport}
                  />
                ))
              )}
              {activeTab === 'students' && (
                <StudentsTab sessions={sessions} intakeCode={selectedIntake} />
              )}
            </div>
          </div>
        </>
      )}

      {/* empty state */}
      {!loading && reportMode === 'intake' && selectedIntake && sessions.length === 0 && (
        <div className="tmis-card text-center py-5" style={{ color: C.muted }}>
          <i className="bi bi-calendar-x fs-2 mb-2 d-block" />
          No ended sessions found for <strong>{selectedIntake}</strong>.
        </div>
      )}

      {!loading && reportMode === 'trainer' && trainerSearched && selectedTrainer && trainerSessions.length === 0 && (
        <div className="tmis-card text-center py-5" style={{ color: C.muted }}>
          <i className="bi bi-calendar-x fs-2 mb-2 d-block" />
          No ended sessions found for the selected trainer in this date range.
        </div>
      )}

      {/* prompt */}
      {!loading && reportMode === 'intake' && !selectedIntake && (
        <div className="tmis-card text-center py-5" style={{ color: C.muted }}>
          <i className="bi bi-bar-chart fs-2 mb-2 d-block" />
          Select an intake above to view attendance reports.
        </div>
      )}

      {!loading && reportMode === 'trainer' && !trainerSearched && (
        <div className="tmis-card text-center py-5" style={{ color: C.muted }}>
          <i className="bi bi-person-lines-fill fs-2 mb-2 d-block" />
          Select a trainer and date range to view attendance rates.
        </div>
      )}
    </div>
  );
}
